import {
  getAccessibleSites,
  getMe,
  type MeResponse,
  type SitePermissions,
} from '@lms/api-client/backend';
import useSWR from 'swr';

// Current authenticated user, or undefined when not logged in. Mirrors
// `getCurrentUser()` from the Next app; a 401 means "signed out", not an error.
export function useCurrentUser() {
  const { data, isLoading, mutate } = useSWR<MeResponse | undefined>(
    'auth/me',
    () => getMe(),
    { shouldRetryOnError: false },
  );
  return { user: data, loading: isLoading, refresh: mutate };
}

export interface SiteAccess extends SitePermissions {
  superAdmin: boolean;
}

// What the visitor may do on a specific site slug. Having the site in their
// accessible-sites list grants view access; the flags grant writes.
// super-admins can do everything everywhere. `access` is undefined while
// loading or when the visitor has no access to the site.
export function useSiteAccess(slug: string | undefined): {
  access: SiteAccess | undefined;
  loading: boolean;
} {
  const { user, loading: userLoading } = useCurrentUser();
  const { data: sites, isLoading: sitesLoading } = useSWR(
    user && !user.superAdmin && slug ? ['auth/me/sites', slug] : null,
    () => getAccessibleSites(),
  );

  if (!slug || !user?.id) return { access: undefined, loading: userLoading };
  if (user.superAdmin) {
    return {
      access: { superAdmin: true, canManageGroups: true, canManageUsers: true },
      loading: userLoading,
    };
  }

  const site = sites?.find((s) => s.siteSlug === slug);
  return {
    access: site && {
      superAdmin: false,
      canManageGroups: site.canManageGroups,
      canManageUsers: site.canManageUsers,
    },
    loading: userLoading || sitesLoading,
  };
}
